import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Grid, Group, NavLink, Paper, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useMyPermissions } from '../../hooks/usePermission';
import { useDevice } from '../../hooks/useDevice';
import {
  ComputedRoleKey,
  useDeleteRole,
  useRoles,
  useRolesCatalog,
  useSaveRole,
} from '../../hooks/useRolesApi';
import { getErrorMessage } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { RoleEditor } from './RoleEditor';
import { grantsOf } from './shared';

type Selected =
  | { kind: 'role'; id: string }
  | { kind: 'computed'; key: ComputedRoleKey }
  | { kind: 'new' };
const COMPUTED: ComputedRoleKey[] = ['employee', 'manager', 'managerOfManagers'];

export const RolesTab: React.FC = () => {
  const { t } = useTranslation();
  const { data: catalog } = useRolesCatalog();
  const { data: roles = [] } = useRoles();
  const { data: perms } = useMyPermissions();
  const save = useSaveRole();
  const remove = useDeleteRole();
  const [selected, setSelected] = useState<Selected | null>(null);
  const isStacked = useDevice() !== 'desktop';
  const editorRef = useRef<HTMLDivElement>(null);
  // Список и редактор друг под другом: выбранную роль показываем, иначе клик «ничего не делает».
  useEffect(() => {
    if (selected && isStacked) editorRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected, isStacked]);

  const current = selected ?? (roles[0] ? ({ kind: 'role', id: roles[0]._id } as Selected) : null);
  const role = current?.kind === 'role' ? roles.find((r) => r._id === current.id) : undefined;
  const admins = roles.find((r) => r.isAll);
  const onError = (e: unknown) =>
    showToast({ title: t('common.error'), message: getErrorMessage(e), color: 'red' });

  if (!catalog) return null;
  const mine = perms?.grants ?? {};

  const editor = (() => {
    if (current?.kind === 'new')
      return (
        <RoleEditor
          key="new"
          catalog={catalog}
          mine={mine}
          title={t('roles.editor.newTitle')}
          initial={{ name: '', grants: [] }}
          isNew
          isSaving={save.isPending}
          onSave={(body) =>
            save.mutate(body, {
              onSuccess: (r) => {
                showToast({ title: t('roles.editor.created'), message: r.name, color: 'teal' });
                setSelected({ kind: 'role', id: r._id });
              },
              onError,
            })
          }
        />
      );
    if (current?.kind === 'computed')
      return (
        <RoleEditor
          key={current.key}
          catalog={catalog}
          mine={mine}
          title={t(`roles.computed.${current.key}.name`)}
          initial={{ name: '', grants: catalog.computedRoles[current.key] }}
          readOnlyNote={t(`roles.computed.${current.key}.note`)}
        />
      );
    if (!role) return null;
    return (
      <RoleEditor
        // Ключ с updatedAt не нужен: после сохранения форма и так совпадает с сервером.
        key={role._id}
        catalog={catalog}
        mine={mine}
        title={role.name}
        initial={{
          name: role.name,
          description: role.description,
          grants: grantsOf(role, catalog),
        }}
        readOnlyNote={
          role.isAll
            ? t(role.except.length ? 'roles.editor.allNoteExcept' : 'roles.editor.allNote', {
                except: role.except.map((k) => t(`roles.permissions.${k}`)).join(', '),
              })
            : undefined
        }
        isSaving={save.isPending}
        onSave={(body) =>
          save.mutate(
            { id: role._id, ...body },
            {
              onSuccess: () =>
                showToast({ title: t('roles.editor.saved'), message: body.name, color: 'teal' }),
              onError,
            },
          )
        }
        onDelete={() =>
          modals.openConfirmModal({
            title: t('roles.editor.deleteTitle'),
            centered: true,
            children: <Text size="sm">{t('roles.editor.deleteText', { name: role.name })}</Text>,
            labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () =>
              remove.mutate(role._id, { onSuccess: () => setSelected(null), onError }),
          })
        }
      />
    );
  })();

  return (
    <Grid gutter="lg">
      <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
        <Stack gap="md">
          <Paper withBorder radius="md" p="xs">
            {roles.map((r) => (
              <NavLink
                key={r._id}
                active={current?.kind === 'role' && current.id === r._id}
                onClick={() => setSelected({ kind: 'role', id: r._id })}
                label={r.name}
                description={t('roles.holders', { count: r.holders })}
                rightSection={
                  r.isTemplate && (
                    <Badge size="xs" variant="light" color="gray" tt="none" fw={500}>
                      {t('roles.template')}
                    </Badge>
                  )
                }
                color="teal"
                style={{ borderRadius: 'var(--mantine-radius-sm)' }}
              />
            ))}
            <Button
              variant="subtle"
              color="teal"
              fullWidth
              justify="flex-start"
              leftSection={<IconPlus size={16} />}
              mt={4}
              onClick={() => setSelected({ kind: 'new' })}
            >
              {t('roles.newRole')}
            </Button>
          </Paper>

          <Paper withBorder radius="md" p="xs">
            <Group gap={6} px="sm" pt={4} pb={2}>
              <IconUsersGroup size={16} color="var(--mantine-color-dimmed)" />
              <Text size="sm" fw={600}>
                {t('roles.computedTitle')}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" px="sm" pb="xs">
              {t('roles.computedHint')}
            </Text>
            {COMPUTED.map((key) => (
              <NavLink
                key={key}
                active={current?.kind === 'computed' && current.key === key}
                onClick={() => setSelected({ kind: 'computed', key })}
                label={t(`roles.computed.${key}.name`)}
                description={t(`roles.computed.${key}.who`)}
                color="teal"
                style={{ borderRadius: 'var(--mantine-radius-sm)' }}
              />
            ))}
          </Paper>

          {admins && admins.holders < 2 && (
            <Text size="sm" c="dimmed" px="xs">
              {t('roles.twoAdmins')}
            </Text>
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8, xl: 9 }} ref={editorRef} style={{ scrollMarginTop: 72 }}>
        {editor}
      </Grid.Col>
    </Grid>
  );
};
